// © 2025 Camp Promethean, LLC. All Rights Reserved.

import { ReactNode, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

interface AdminRouteGuardProps {
  children: ReactNode;
}

export function AdminRouteGuard({ children }: AdminRouteGuardProps) {
  const { isAuthenticated, loading, profile } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (loading) return;
    if (!isAuthenticated) {
      toast.error("Sign in first", {
        description: "Admin tools require a logged-in account.",
      });
      navigate("/login");
      return;
    }
    if (profile?.role !== "admin") {
      toast.error("Admins only", {
        description: "That area is limited to administrators.",
      });
      navigate("/dashboard");
    }
  }, [isAuthenticated, loading, profile?.role, navigate]);

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <span className="ml-2">Verifying admin access…</span>
      </div>
    );
  }

  return isAuthenticated && profile?.role === "admin" ? <>{children}</> : null;
}
